import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBoxOpen, faCheck, faClose, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import ModalPortal from "../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { useConfirmation } from "../../context/useConfirmationContext"
import type { AddtionalEntity } from "../../Objects/Addtional/AddtionalEntity"
import type { AddtionalStockEntity } from "../../Objects/Addtional/AddtionalStockEntity"

interface AdditionalStockPopupProps {
  addtionals: AddtionalEntity[]
  canEdite: boolean
  pendingCount: number
  getAdditionalStockConfig: (additional: AddtionalEntity) => AddtionalStockEntity
  onAdditionalStockControlChange: (additional: AddtionalEntity, hasStockControl: boolean) => void
  onAdditionalStockQuantityChange: (additional: AddtionalEntity, stockQuantity: number) => void
  onSaveAll: () => Promise<boolean>
  onClose: () => void
}

const rowGridClass = "grid min-w-[640px] grid-cols-[minmax(160px,1fr)_140px_160px_130px] items-center gap-2"

const quantityFieldId = (additionalId: number) => `additional-stock-quantity-${additionalId}`

const toggleFieldId = (additionalId: number) => `additional-stock-toggle-${additionalId}`

export const AdditionalStockPopup: React.FC<AdditionalStockPopupProps> = ({
  addtionals,
  canEdite,
  pendingCount,
  getAdditionalStockConfig,
  onAdditionalStockControlChange,
  onAdditionalStockQuantityChange,
  onSaveAll,
  onClose,
}) => {
  const { t } = useTranslation("genericFoodEntity")

  const [search, setSearch] = useState("")

  const { setNewConfirmation } = useConfirmation()

  const filteredAddtionals = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()

    return addtionals.filter((additional) =>
      normalizedSearch ? additional.name.toLowerCase().includes(normalizedSearch) : true,
    )
  }, [addtionals, search])

  const orderedAdditionalIds = useMemo(() => filteredAddtionals.map((additional) => additional.id), [filteredAddtionals])

  const requestClose = () => {
    if (pendingCount > 0) {
      setNewConfirmation(
        t("label_additional_stock_close_confirmation_title"),
        t("label_additional_stock_close_confirmation_description"),
        onClose,
      )
    } else {
      onClose()
    }
  }

  const focusNextRow = (currentAdditionalId: number) => {
    const nextAdditionalId = orderedAdditionalIds[orderedAdditionalIds.indexOf(currentAdditionalId) + 1]

    if (nextAdditionalId === undefined) return

    const nextElement =
      document.getElementById(quantityFieldId(nextAdditionalId)) ??
      document.getElementById(toggleFieldId(nextAdditionalId))

    nextElement?.focus()
  }

  const handleQuantityKeyDown = (additional: AddtionalEntity) => (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      focusNextRow(additional.id)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t("title_additional_stock_popup")}
              icon={faBoxOpen}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={requestClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.products.additionalStockPopup.additionalStockPopup.button.requestClose"
                />
              }
            />

            <div className="flex flex-col gap-3 p-2.5">
              <Animate type={AnimationTypes.fadeInTraslate} duration="0.35s" delay="0.08s">
                <InputField
                  type="text"
                  placeholder={t("label_additional_stock_search_placeholder")}
                  icon={faMagnifyingGlass}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.products.additionalStockPopup.additionalStockPopup.input.search"
                />
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                <section className="overflow-x-auto rounded-lg border border-slate-200">
                  <div className={`${rowGridClass} bg-slate-100 px-2.5 py-2 text-xs font-extrabold text-slate-700`}>
                    <span>{t("label_additional_stock_column_additional")}</span>
                    
                    <span>{t("label_additional_stock_column_control")}</span>
                    
                    <span>{t("label_additional_stock_column_quantity")}</span>
                  
                    <span>{t("label_additional_stock_column_status")}</span>
                  </div>

                  {filteredAddtionals.length === 0 && (
                    <div className="px-2.5 py-4 text-center text-sm text-slate-500">
                      {t("label_additional_stock_empty_search")}
                    </div>
                  )}

                  {filteredAddtionals.map((additional) => {
                    const stock = getAdditionalStockConfig(additional)

                    const status = !stock.hasStockControl
                      ? {
                          label: t("label_additional_stock_status_unlimited"),
                          className: "bg-slate-100 text-slate-600",
                        }
                      : stock.isOutOfStock
                        ? {
                            label: t("label_additional_stock_status_out_of_stock"),
                            className: "bg-rose-100 text-rose-700",
                          }
                        : {
                            label: t("label_additional_stock_status_in_stock"),
                            className: "bg-emerald-100 text-emerald-700",
                          }

                    const rowIndex = orderedAdditionalIds.indexOf(additional.id)

                    return (
                      <Animate
                        key={additional.id}
                        type={AnimationTypes.fadeInTraslate}
                        duration="0.3s"
                        delay={`${Math.min(rowIndex, 10) * 0.03}s`}
                      >
                        <div className={`${rowGridClass} border-t border-slate-200 px-2.5 py-2`}>
                          <span className="truncate text-sm text-slate-800">{additional.name}</span>

                          <input
                            id={toggleFieldId(additional.id)}
                            className="h-4 w-4 accent-orange-600"
                            type="checkbox"
                            checked={stock.hasStockControl}
                            disabled={canEdite}
                            aria-label={t("label_additional_has_stock_control")}
                            onChange={(event) => onAdditionalStockControlChange(additional, event.target.checked)}
                            data-interaction-description-key="interactionDescriptions:pages.products.additionalStockPopup.additionalStockPopup.toggle.hasStockControl"
                          />

                          <div onKeyDown={handleQuantityKeyDown(additional)}>
                            <InputField
                              id={quantityFieldId(additional.id)}
                              type="number"
                              placeholder={t("label_additional_stock_quantity_place_holder")}
                              onInvalidMessage={t("label_stock_quantity_onInvalidMessage")}
                              value={String(stock.stockQuantity)}
                              onChange={(event) =>
                                onAdditionalStockQuantityChange(additional, Number(event.target.value) || 0)
                              }
                              disable={canEdite || !stock.hasStockControl}
                              min={0}
                              descriptionKey="interactionDescriptions:pages.products.additionalStockPopup.additionalStockPopup.input.quantity"
                            />
                          </div>

                          <span
                            className={`inline-flex w-fit items-center rounded-full px-2 py-1 text-[11px] font-semibold ${status.className}`}
                          >
                            {status.label}
                          </span>
                        </div>
                      </Animate>
                    )
                  })}
                </section>
              </Animate>

              <span className="text-xs font-semibold text-slate-500">{t("label_additional_stock_shortcuts_hint")}</span>

              <div className={defaultStyle.divisionBar}></div>

              <Animate type={AnimationTypes.fadeInTraslate} duration="0.35s" delay="0.12s">
                <section className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-bold text-slate-700">
                    {pendingCount > 0
                      ? t("label_additional_stock_pending_count", { count: pendingCount })
                      : t("label_additional_stock_no_pending")}
                  </span>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-green-600 px-3 text-sm font-bold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-55"
                      onClick={() => void onSaveAll()}
                      disabled={pendingCount === 0 || canEdite}
                      data-interaction-description-key="interactionDescriptions:pages.products.additionalStockPopup.additionalStockPopup.button.saveAll"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      {t("label_additional_stock_save_button")}
                    </button>

                    <button
                      type="button"
                      className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-red-600 px-3 text-sm font-bold text-white transition hover:bg-red-700"
                      onClick={requestClose}
                      data-interaction-description-key="interactionDescriptions:pages.products.additionalStockPopup.additionalStockPopup.button.requestClose2"
                    >
                      <FontAwesomeIcon icon={faClose} />
                      {t("buttons:button_cancel")}
                    </button>
                  </div>
                </section>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
